import { Product } from '../data/productModels';
import { PRODUCTS } from '../data/products'; // Fallback

export const listPublicProducts = async (): Promise<Product[]> => {
  try {
    const res = await fetch('/api/products');
    if (!res.ok) throw new Error('Failed to fetch products');
    return await res.json();
  } catch (e) {
    console.warn('API Error, falling back to static data:', e);
    // Cast fallback to match type if needed, or better yet, ensure type alignment
    return PRODUCTS as unknown as Product[];
  }
};

export const getPublicProductBySlug = async (slug: string): Promise<Product | undefined> => {
  try {
    const res = await fetch(`/api/products/${slug}`);
    if (!res.ok) {
      if (res.status === 404) return undefined;
      throw new Error('Failed to fetch product');
    }
    return await res.json();
  } catch (e) {
    console.warn('API Error, falling back to static data:', e);
    return PRODUCTS.find(p => p.slug === slug) as unknown as Product;
  }
};

type CacheEntry = { product: Product | undefined; ts: number };
const CACHE_TTL_MS = 60_000;
const productCache = new Map<string, CacheEntry>();

export const getPublicProductsBySlugs = async (
  slugs: string[]
): Promise<Record<string, Product | undefined>> => {
  const out: Record<string, Product | undefined> = {};
  if (!slugs.length) return out;

  const now = Date.now();
  const missing: string[] = [];
  for (const slug of slugs) {
    const hit = productCache.get(slug);
    if (hit && now - hit.ts < CACHE_TTL_MS) {
      out[slug] = hit.product;
    } else {
      missing.push(slug);
    }
  }
  if (missing.length === 0) return out;

  try {
    const res = await fetch('/api/products/batch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slugs: missing }),
    });
    if (!res.ok) throw new Error('Failed to fetch products batch');
    const payload = await res.json();
    const list: Product[] = Array.isArray(payload?.products) ? payload.products : [];
    for (const slug of missing) {
      const product = list.find(p => p.slug === slug);
      out[slug] = product;
      productCache.set(slug, { product, ts: now });
    }
    return out;
  } catch (e) {
    console.warn('Batch API error, falling back to static data:', e);
    for (const slug of missing) {
      out[slug] = PRODUCTS.find(p => p.slug === slug) as unknown as Product;
    }
    return out;
  }
};
